<template>
  <div v-if="!loading" class="admission-portal">
    <!-- Header -->
    <header class="portal-header">
      <div class="container-fluid">
        <div class="header-content">
          <div class="app-info">
            <span class="app-label">Application ID:</span>
            <span class="app-id">{{ applicationId }}</span>
          </div>
          <button @click="handleLogout" class="btn-logout">
            <i class="fa fa-sign-out"></i>
            <span>Logout</span>
          </button>
        </div>
      </div>
    </header>

    <!-- Tab Navigation -->
    <div class="tab-navigation">
      <div class="container-fluid">
        <div class="tab-nav-wrapper">
          <button v-for="tab in visibleTabs" :key="tab.id" @click="activeTab = tab.id"
            :class="['tab-btn', { active: activeTab === tab.id }]">
            <i :class="tab.icon"></i>
            <span>{{ tab.label }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Tab Content -->
    <div class="container-fluid tab-content-wrapper">

      <!-- Guidelines Tab -->
      <div v-if="activeTab === 'guidelines'" class="tab-content active">
        <div class="content-card">
          <h2 class="section-title">
            <i class="fa fa-file-text-o"></i>
            Application Guidelines
          </h2>

          <div class="guideline-content">
            <div class="alert-box alert-info">
              <h3><i class="fa fa-calendar"></i> Important Deadline</h3>
              <p>Application Deadline: <strong>{{ formatDate(applicationDeadline) }}</strong></p>
            </div>

            <div class="guideline-section">
              <h3>Required Documents:</h3>
              <ul class="guideline-list">
                <li><strong>Proof of Identity:</strong> Passport/Birth Registration/NID (PDF only)</li>
                <li><strong>A Level Documents:</strong>
                  <ul style="margin-top: 8px; margin-left:12px; list-style:square;">
                    <li>Statement of Entry (PDF only)</li>
                    <li>Transcript/Gradesheet (PDF only)</li>
                    <li>Certificate [If exists] (PDF only)</li>
                  </ul>
                </li>
                <li><strong>O Level Documents:</strong>
                  <ul style="margin-top: 8px; margin-left:12px; list-style:square;">
                    <li>Statement of Entry (PDF only)</li>
                    <li>Transcript/Gradesheet (PDF only)</li>
                    <li>Certificate (PDF only)</li>
                  </ul>
                </li>
                  <strong>For General Board (SSC / HSC)</strong>

                  <ul style="margin-top: 8px; margin-left:12px; list-style:square;">
                    <li>Admit Card  (PDF only)</li>
                    <li>Transcript/Gradesheet (PDF only)</li>
                    <li>Certificate [Mendatory for SSC] (PDF only)</li>
                  </ul>
                <li>
                </li>
                <li>File size should not exceed 5 MB per document</li>
                <li>Documents should be clear and readable</li>
              </ul>
            </div>

            <div class="guideline-section">
              <h3>Instructions:</h3>
              <ol class="guideline-list">
                <li>Fill all required fields accurately</li>
                <li>Add all A Level and O Level subjects with grades / Points</li>
                <li>Upload clear, legible PDF copies of all certificates</li>
                <li>Review your application before submission</li>
                <li>After clicking "Apply Now", you will be redirected to the payment page</li>
                <li>Documents will be uploaded after successful payment</li>
                <li>Keep your Application ID and payment receipt safe</li>
              </ol>
            </div>

            <div class="alert-box alert-warning">
              <h3><i class="fa fa-exclamation-triangle"></i> Important Note</h3>
              <p>Documents will only be uploaded to the system after clicking the "Apply Now" button and completing the
                payment process. Your application status will show as "Due" until payment is completed.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Application Form Tab -->
      <div v-if="activeTab === 'application'" class="tab-content active">
        <div class="content-card">
          <h2 class="section-title">
            <i class="fa fa-wpforms"></i>
            Application Form
          </h2>

          <!-- Payment Status -->
          <div v-if="applicationStatus.submitted" class="payment-status-card">
            <div class="payment-info">
              <div class="payment-item">
                <label>Bill Number:</label>
                <span class="bill-number">{{ paymentInfo.billNumber }}</span>
              </div>
              <div class="payment-item">
                <label>Amount:</label>
                <span class="amount">৳ {{ paymentInfo.amount }}</span>
              </div>
              <div class="payment-item">
                <label>Status:</label>
                <span :class="['badge', paymentInfo.status === 'paid' ? 'badge-success' : 'badge-danger']">
                  {{ paymentInfo.status.toUpperCase() }}
                </span>
              </div>
            </div>
          </div>

          <div v-if="isDeadlinePassed" class="alert-box alert-danger">
            <p><strong>Application deadline has passed.</strong> New submissions are no longer accepted.</p>
          </div>

          <form @submit.prevent="handleSubmit" class="application-form">

            <!-- Personal Information -->
            <div class="form-section">
              <h3 class="form-section-title">Personal Information</h3>
              <div class="row">
                <div class="col-md-6 col-sm-12 form-group">
                  <label class="modern-label">Name (English) <span class="required">*</span></label>
                  <div class="input-wrapper">
                    <i class="fa fa-user input-icon"></i>
                    <input type="text" v-model="formData.name" class="modern-input" placeholder="Enter your full name"
                      :disabled="isDeadlinePassed || applicationStatus.submitted" required />
                  </div>
                </div>

                <div class="col-md-6 col-sm-12 form-group">
                  <label class="modern-label">Father's Name (English) <span class="required">*</span></label>
                  <div class="input-wrapper">
                    <i class="fa fa-user input-icon"></i>
                    <input type="text" v-model="formData.fatherName" class="modern-input"
                      placeholder="Enter father's name" :disabled="isDeadlinePassed || applicationStatus.submitted"
                      required />
                  </div>
                </div>

                <div class="col-md-6 col-sm-12 form-group">
                  <label class="modern-label">Mother's Name (English) <span class="required">*</span></label>
                  <div class="input-wrapper">
                    <i class="fa fa-user input-icon"></i>
                    <input type="text" v-model="formData.motherName" class="modern-input"
                      placeholder="Enter mother's name" :disabled="isDeadlinePassed || applicationStatus.submitted"
                      required />
                  </div>
                </div>

                <div class="col-md-6 col-sm-12 form-group">
                  <label class="modern-label">Date of Birth <span class="required">*</span></label>
                  <div class="input-wrapper">
                    <i class="fa fa-calendar input-icon"></i>
                    <input type="date" v-model="formData.dateOfBirth" class="modern-input"
                      :disabled="isDeadlinePassed || applicationStatus.submitted" required />
                  </div>
                </div>

                <div class="col-md-6 col-sm-12 form-group">
                  <label class="modern-label">Gender <span class="required">*</span></label>
                  <select v-model="formData.gender" class="modern-select"
                    :disabled="isDeadlinePassed || applicationStatus.submitted" required>
                    <option value="">Select Gender</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                  </select>
                </div>

                <div class="col-md-6 col-sm-12 form-group">
                  <label class="modern-label">O Level Passing Year <span class="required">*</span></label>
                  <select v-model="formData.oLevelPassYear" class="modern-select"
                    :disabled="isDeadlinePassed || applicationStatus.submitted" required>
                    <option value="">Select Year</option>
                    <option value="2023">2023</option>
                    <option value="2022">2022</option>
                  </select>
                </div>

                <div class="col-md-6 col-sm-12 form-group">
                  <label class="modern-label">A Level Passing Year <span class="required">*</span></label>
                  <select v-model="formData.aLevelPassYear" class="modern-select"
                    :disabled="isDeadlinePassed || applicationStatus.submitted" required>
                    <option value="">Select Year</option>
                    <option value="2025">2025</option>
                    <option value="2024">2024</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- O Level Subjects -->
            <div class="form-section">
              <h3 class="form-section-title">O Level Subject Information</h3>
              
              <p class="text-danger" v-if="!(isDeadlinePassed || applicationStatus.submitted)"><small>(Optional)</small></p>
              <!-- <p class="text-danger" v-if="!(isDeadlinePassed || applicationStatus.submitted)"><small>* All subject
                  information is required</small></p> -->

              <div v-if="sscType === 'GEN'" class="alert-box mt-2 alert-warning">
                <p><strong>Note:</strong> O Level subjects are not applicable for your qualification type. This section
                  is disabled.</p>
              </div>

              <div class="table-responsive">
                <table class="subject-table">
                  <thead>
                    <tr>
                      <th style="width: 8%">Serial</th>
                      <th style="width: 50%">Subject Name</th>
                      <th style="width: 15%">Grade</th>
                      <th style="width: 15%">Grade Point</th>
                      <th v-if="!(isDeadlinePassed || applicationStatus.submitted)" style="width: 12%">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(subject, index) in formData.oLevelSubjects" :key="subject.id">
                      <td>{{ index + 1 }}</td>
                      <td>
                        <select v-model="subject.name" @change="updateGradePoint('oLevelSubjects', subject.id)"
                          class="table-select"
                          :disabled="isDeadlinePassed || applicationStatus.submitted || sscType === 'GEN'">
                          <option value="">Select Subject</option>
                          <option v-for="subj in oLevelSubjects" :key="subj.value" :value="subj.value">
                            {{ subj.label }}
                          </option>
                          <option value="OTHER">OTHER</option>
                        </select>
                        <input v-if="subject.name === 'OTHER'" type="text" v-model="subject.customName"
                          class="table-input mt-2" placeholder="Enter subject name" :disabled="isDeadlinePassed || applicationStatus.submitted || sscType === 'GEN'" />
                      </td>
                      <td>
                        <select v-model="subject.grade" @change="updateGradePoint('oLevelSubjects', subject.id)"
                          class="table-select"
                          :disabled="isDeadlinePassed || applicationStatus.submitted || sscType === 'GEN'">
                          <option value="">Grade</option>
                          <option value="A*">A*</option>
                          <option value="A">A</option>
                          <option value="B">B</option>
                          <option value="C">C</option>
                          <option value="D">D</option>
                          <option value="F">F</option>
                          <option value="OTHER">OTHER</option>
                        </select>
                        <input v-if="subject.grade === 'OTHER'" type="text" v-model="subject.customGrade"
                          class="table-input mt-2" placeholder="Enter grade" :disabled="isDeadlinePassed || applicationStatus.submitted || sscType === 'GEN'" />
                      </td>
                      <td>
                        <input type="number" v-model="subject.gradePoint" class="table-input" placeholder="Point"
                          :readonly="subject.grade !== 'OTHER'"
                          :disabled="isDeadlinePassed || applicationStatus.submitted || sscType === 'GEN'" />
                      </td>
                      <td v-if="!(isDeadlinePassed || applicationStatus.submitted)" class="text-center">
                        <button type="button" @click="removeSubject('oLevelSubjects', subject.id)" class="btn-delete"
                          :disabled="isDeadlinePassed || applicationStatus.submitted || sscType === 'GEN'">
                          <i class="fa fa-trash"></i> Remove
                        </button>
                      </td>
                    </tr>
                    <tr v-if="formData.oLevelSubjects.length === 0">
                      <td colspan="5" class="text-center text-muted">No subjects added yet</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <button v-if="!(isDeadlinePassed || applicationStatus.submitted)" type="button"
                @click="addSubject('oLevelSubjects')" class="btn-add-subject" :disabled="sscType === 'GEN'">
                <i class="fa fa-plus"></i> Add Subject
              </button>
            </div>

            <!-- A Level Subjects -->
            <div class="form-section">
              <h3 class="form-section-title">A Level Subject Information</h3>
              <p class="text-danger" v-if="!(isDeadlinePassed || applicationStatus.submitted)"><small>(Optional)</small></p>

              <div v-if="hscType === 'GEN'" class="alert-box mt-2 alert-warning">
                <p><strong>Note:</strong> A Level subjects are not applicable for your qualification type. This section
                  is disabled.</p>
              </div>

              <div class="table-responsive">
                <table class="subject-table">
                  <thead>
                    <tr>
                      <th style="width: 8%">Serial</th>
                      <th style="width: 50%">Subject Name</th>
                      <th style="width: 15%">Grade</th>
                      <th style="width: 15%">Grade Point</th>
                      <th v-if="!(isDeadlinePassed || applicationStatus.submitted)" style="width: 12%">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(subject, index) in formData.aLevelSubjects" :key="subject.id">
                      <td>{{ index + 1 }}</td>
                      <td>
                        <select v-model="subject.name" @change="updateGradePoint('aLevelSubjects', subject.id)"
                          class="table-select"
                          :disabled="isDeadlinePassed || applicationStatus.submitted || hscType === 'GEN'">
                          <option value="">Select Subject</option>
                          <option v-for="subj in aLevelSubjects" :key="subj.value" :value="subj.value">
                            {{ subj.label }}
                          </option>
                          <option value="OTHER">OTHER</option>
                        </select>
                        <input v-if="subject.name === 'OTHER'" type="text" v-model="subject.customName"
                          class="table-input mt-2" placeholder="Enter subject name" :disabled="isDeadlinePassed || applicationStatus.submitted || hscType === 'GEN'" />
                      </td>
                      <td>
                        <select v-model="subject.grade" @change="updateGradePoint('aLevelSubjects', subject.id)"
                          class="table-select"
                          :disabled="isDeadlinePassed || applicationStatus.submitted || hscType === 'GEN'">
                          <option value="">Grade</option>
                          <option value="A">A</option>
                          <option value="B">B</option>
                          <option value="C">C</option>
                          <option value="D">D</option>
                          <option value="F">F</option>
                          <option value="OTHER">OTHER</option>
                        </select>
                        <input v-if="subject.grade === 'OTHER'" type="text" v-model="subject.customGrade"
                          class="table-input mt-2" placeholder="Enter grade" :disabled="isDeadlinePassed || applicationStatus.submitted || hscType === 'GEN'" />
                      </td>
                      <td>
                        <input type="number" v-model="subject.gradePoint" class="table-input" placeholder="Point"
                          :readonly="subject.grade !== 'OTHER'"
                          :disabled="isDeadlinePassed || applicationStatus.submitted || hscType === 'GEN'" />
                      </td>
                      <td v-if="!(isDeadlinePassed || applicationStatus.submitted)" class="text-center">
                        <button type="button" @click="removeSubject('aLevelSubjects', subject.id)" class="btn-delete"
                          :disabled="isDeadlinePassed || applicationStatus.submitted || hscType === 'GEN'">
                          <i class="fa fa-trash"></i> Remove
                        </button>
                      </td>
                    </tr>
                    <tr v-if="formData.aLevelSubjects.length === 0">
                      <td colspan="5" class="text-center text-muted">No subjects added yet</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <button v-if="!(isDeadlinePassed || applicationStatus.submitted)" type="button"
                @click="addSubject('aLevelSubjects')" class="btn-add-subject" :disabled="hscType === 'GEN'">
                <i class="fa fa-plus"></i> Add Subject
              </button>
            </div>

            <!-- Document Upload -->
            <div v-if="!(isDeadlinePassed || applicationStatus.submitted)" class="form-section">
              <h3 class="form-section-title">Upload Required Documents</h3>
              <p class="text-danger py-2"><small>* PDF files only | Maximum file size: 5 MB per document</small></p>

              <!-- Proof of Identity -->
              <div class="identity-upload-section">
                <h4 class="subsection-title">Proof of Identity</h4>
                <div class="row">
                  <div class="col-md-4 col-sm-12 form-group">
                    <label class="modern-label">Document Type <span class="required">*</span></label>
                    <select v-model="uploadedFiles.identityProof.type" class="modern-select"
                      :disabled="isDeadlinePassed || applicationStatus.submitted" required>
                      <option value="">Select Type</option>
                      <option value="Passport">Passport</option>
                      <option value="BirthRegistration">Birth Registration</option>
                      <option value="NID">National ID (NID)</option>
                    </select>
                  </div>
                  <div class="col-md-8 col-sm-12">
                    <div class="upload-box-inline">
                      <label class="modern-label">Upload Document <span class="required">*</span></label>
                      <div class="file-upload-wrapper">
                        <label class="file-upload-btn">
                          <i class="fa fa-cloud-upload"></i>
                          <span>{{ uploadedFiles.identityProof.file ? uploadedFiles.identityProof.file.name : 'Choose PDF file' }}</span>
                          <input type="file" @change="handleFileUpload($event, 'identityProof')"
                            accept="application/pdf" :disabled="isDeadlinePassed || applicationStatus.submitted" />
                        </label>
                        <div v-if="uploadedFiles.identityProof.file" class="file-uploaded-badge">
                          <i class="fa fa-check-circle"></i> Uploaded
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- O Level Documents -->
              <div class="document-group">
                <h4 class="subsection-title">
                  {{ sscType === 'GEN' ? 'SSC Documents' : 'O Level Documents (SSC Equivalent)' }}
                </h4>
                <div class="row">
                  <div class="col-md-4 col-sm-12">
                    <div class="upload-box-compact">
                      <label class="doc-label">
                        {{ sscType === 'GEN' ? 'SSC Admit Card' : 'Statement of Entry (O Level)' }}
                        <span class="required">*</span></label>
                      <label class="file-upload-btn-compact">
                        <i class="fa fa-file-pdf-o"></i>
                        <span>{{ uploadedFiles.oLevelStatementOfEntry ? uploadedFiles.oLevelStatementOfEntry.name :
                          'Upload PDF' }}</span>
                        <input type="file" @change="handleFileUpload($event, 'oLevelStatementOfEntry')"
                          accept="application/pdf" :disabled="isDeadlinePassed || applicationStatus.submitted" />
                      </label>
                      <div v-if="uploadedFiles.oLevelStatementOfEntry" class="file-success">
                        <i class="fa fa-check-circle"></i> {{ (uploadedFiles.oLevelStatementOfEntry.size /
                          1024).toFixed(0) }} KB
                      </div>
                    </div>
                  </div>

                  <div class="col-md-4 col-sm-12">
                    <div class="upload-box-compact">
                      <label class="doc-label">Transcript/Gradesheet <span class="required">*</span></label>
                      <label class="file-upload-btn-compact">
                        <i class="fa fa-file-pdf-o"></i>
                        <span>{{ uploadedFiles.oLevelTranscript ? uploadedFiles.oLevelTranscript.name : 'Upload PDF'
                        }}</span>
                        <input type="file" @change="handleFileUpload($event, 'oLevelTranscript')"
                          accept="application/pdf" :disabled="isDeadlinePassed || applicationStatus.submitted" />
                      </label>
                      <div v-if="uploadedFiles.oLevelTranscript" class="file-success">
                        <i class="fa fa-check-circle"></i> {{ (uploadedFiles.oLevelTranscript.size / 1024).toFixed(0) }}
                        KB
                      </div>
                    </div>
                  </div>

                  <div class="col-md-4 col-sm-12">
                    <div class="upload-box-compact">
                      <label class="doc-label">Certificate <span class="required">*</span></label>
                      <label class="file-upload-btn-compact">
                        <i class="fa fa-file-pdf-o"></i>
                        <span>{{ uploadedFiles.oLevelCertificate ? uploadedFiles.oLevelCertificate.name : 'Upload PDF'
                        }}</span>
                        <input type="file" @change="handleFileUpload($event, 'oLevelCertificate')"
                          accept="application/pdf" :disabled="isDeadlinePassed || applicationStatus.submitted" />
                      </label>
                      <div v-if="uploadedFiles.oLevelCertificate" class="file-success">
                        <i class="fa fa-check-circle"></i> {{ (uploadedFiles.oLevelCertificate.size / 1024).toFixed(0)
                        }} KB
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- A Level Documents -->
              <div class="document-group">
                <h4 class="subsection-title">
                  {{ hscType === 'GEN' ? 'HSC Documents' : 'A Level Documents (HSC Equivalent)' }}
                </h4>
                <div class="row">
                  <div class="col-md-4 col-sm-12">
                    <div class="upload-box-compact">
                      <label class="doc-label">
                        {{ hscType === 'GEN' ? 'HSC Admit Card' : 'Statement of Entry (A Level)' }}
                        <span class="required">*</span></label>
                      <label class="file-upload-btn-compact">
                        <i class="fa fa-file-pdf-o"></i>
                        <span>{{ uploadedFiles.aLevelStatementOfEntry ? uploadedFiles.aLevelStatementOfEntry.name :
                          'Upload PDF' }}</span>
                        <input type="file" @change="handleFileUpload($event, 'aLevelStatementOfEntry')"
                          accept="application/pdf" :disabled="isDeadlinePassed || applicationStatus.submitted" />
                      </label>
                      <div v-if="uploadedFiles.aLevelStatementOfEntry" class="file-success">
                        <i class="fa fa-check-circle"></i> {{ (uploadedFiles.aLevelStatementOfEntry.size /
                          1024).toFixed(0) }} KB
                      </div>
                    </div>
                  </div>

                  <div class="col-md-4 col-sm-12">
                    <div class="upload-box-compact">
                      <label class="doc-label">Transcript/Gradesheet <span class="required">*</span></label>
                      <label class="file-upload-btn-compact">
                        <i class="fa fa-file-pdf-o"></i>
                        <span>{{ uploadedFiles.aLevelTranscript ? uploadedFiles.aLevelTranscript.name : 'Upload PDF'
                        }}</span>
                        <input type="file" @change="handleFileUpload($event, 'aLevelTranscript')"
                          accept="application/pdf" :disabled="isDeadlinePassed || applicationStatus.submitted" />
                      </label>
                      <div v-if="uploadedFiles.aLevelTranscript" class="file-success">
                        <i class="fa fa-check-circle"></i> {{ (uploadedFiles.aLevelTranscript.size / 1024).toFixed(0) }}
                        KB
                      </div>
                    </div>
                  </div>

                  <div class="col-md-4 col-sm-12">
                    <div class="upload-box-compact">
                      <label class="doc-label">Certificate</label>
                      <label class="file-upload-btn-compact">
                        <i class="fa fa-file-pdf-o"></i>
                        <span>{{ uploadedFiles.aLevelCertificate ? uploadedFiles.aLevelCertificate.name : 'Upload PDF'
                        }}</span>
                        <input type="file" @change="handleFileUpload($event, 'aLevelCertificate')"
                          accept="application/pdf" :disabled="isDeadlinePassed || applicationStatus.submitted" />
                      </label>
                      <div v-if="uploadedFiles.aLevelCertificate" class="file-success">
                        <i class="fa fa-check-circle"></i> {{ (uploadedFiles.aLevelCertificate.size / 1024).toFixed(0)
                        }} KB
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            <!-- Submit Section -->
            <div class="submit-section" v-if="!applicationStatus.submitted">
              <div class="submit-info">
                <p>
                  <strong>Before submitting:</strong> Please review all information carefully. After clicking "Apply
                  Now",
                  you will be redirected to the payment page. All documents will be uploaded after successful payment.
                </p>
              </div>
              <button type="submit" class="btn-submit" :disabled="isDeadlinePassed || isSubmitting">
                <i class="fa fa-paper-plane" v-if="!isSubmitting"></i>
                <i class="fa fa-spinner fa-spin" v-else></i>
                {{ isSubmitting ? 'Processing...' : 'Apply Now' }}
              </button>
            </div>
          </form>
        </div>
      </div>


      <!-- Payment Tab -->
      <div v-if="activeTab === 'payment'" class="tab-content active">
         <!-- Payment Status -->
          <div v-if="applicationStatus.submitted" class="payment-status-card">
            <div class="payment-info">
              <div class="payment-item">
                <label>Bill Number:</label>
                <span class="bill-number">{{ paymentInfo.billNumber }}</span>
              </div>
              <div class="payment-item">
                <label>Amount:</label>
                <span class="amount">৳ {{ paymentInfo.amount }}</span>
              </div>
              <div class="payment-item">
                <label>Status:</label>
                <span :class="['badge', paymentInfo.status === 'paid' ? 'badge-success' : 'badge-danger']">
                  {{ paymentInfo.status.toUpperCase() }}
                </span>
              </div>
            </div>
          </div>
        <div class="content-card">
          <h2 class="section-title">
            <i class="fa fa-wpforms"></i>
            Equivalence Application Payment
          </h2>
          <!-- isAcceptingApplications Payment deadline is over message -->
          <div v-if="!isAcceptingPayments" class="alert alert-danger" role="alert">
            <strong>Payment period has ended.</strong> No further payments are being accepted at this time.
          </div>
          <div class="row center-content"
            v-if="isAcceptingPayments && this.applicationStatus.submitted && this.paymentInfo.status === 'due' && !isPayButtonLoading">
            <div class="checkout-page" style="width: 100%; max-width: 900px; margin: 0 auto;">
              <div class="checkout-form theme-form">
                <div class="row center-content">
                  <div class="col-lg-12 col-sm-12 col-xs-12">
                    <div class="theme-card check-out ">
                      <div class="payment-options-container py-4">
                        <h5
                          style="text-align: center; margin-bottom: 25px; color: #2c3e50; font-weight: 600; letter-spacing: 0.5px;">
                          Choose Your Payment Method
                        </h5>

                        <!-- Payment Cards Grid -->
                        <div class="row" style="display: flex; justify-content: center; gap: 10px; flex-wrap: wrap;">

                          <!-- bKash Card -->
                          <div class="col-lg-3 col-md-6 col-sm-6 col-6" style="padding: 5px;">
                            <div class="payment-card" @click="startBkashCheckoutService"
                              style="cursor: pointer; transition: all 0.3s ease; border: 2px solid #e0e0e0; border-radius: 12px; padding: 20px 15px; background: white; box-shadow: 0 2px 8px rgba(0,0,0,0.08); text-align: center; min-height: 140px; display: flex; flex-direction: column; justify-content: center; align-items: center;">
                              <div class="payment-card-logo" style="margin-bottom: 10px;">
                                <img alt="bKash" style="height: 60px; max-width: 100%; object-fit: contain;"
                                  class="img-fluid" src="../../theme-assets/images/logo/bkash.png">
                              </div>
                              <div class="payment-card-title"
                                style="font-size: 14px; font-weight: 600; color: #E2136E; margin-top: 8px;">
                                bKash
                              </div>
                            </div>
                          </div>

                          <!-- Rocket Card -->
                          <div class="col-lg-3 col-md-6 col-sm-6 col-6" style="padding: 5px;">
                            <div class="payment-card" @click="startDbblRocketPayment"
                              style="cursor: pointer; transition: all 0.3s ease; border: 2px solid #e0e0e0; border-radius: 12px; padding: 20px 15px; background: white; box-shadow: 0 2px 8px rgba(0,0,0,0.08); text-align: center; min-height: 140px; display: flex; flex-direction: column; justify-content: center; align-items: center;">
                              <div class="payment-card-logo" style="margin-bottom: 10px;">
                                <img alt="DBBL Rocket" style="height: 60px; max-width: 100%; object-fit: contain;"
                                  class="img-fluid" src="../../theme-assets/images/logo/rocket.png">
                              </div>
                              <div class="payment-card-title"
                                style="font-size: 14px; font-weight: 600; color: #8B3A8B; margin-top: 8px;">
                                Rocket
                              </div>
                            </div>
                          </div>

                          <!-- Visa Card -->
                          <div class="col-lg-3 col-md-6 col-sm-6 col-6" style="padding: 5px;">
                            <div class="payment-card" @click="startDbblVisaPayment"
                              style="cursor: pointer; transition: all 0.3s ease; border: 2px solid #e0e0e0; border-radius: 12px; padding: 20px 15px; background: white; box-shadow: 0 2px 8px rgba(0,0,0,0.08); text-align: center; min-height: 140px; display: flex; flex-direction: column; justify-content: center; align-items: center;">
                              <div class="payment-card-logo" style="margin-bottom: 10px;">
                                <img alt="Visa Card" style="height: 60px; max-width: 100%; object-fit: contain;"
                                  class="img-fluid" src="../../theme-assets/images/logo/visa-sq.png">
                              </div>
                              <div class="payment-card-title"
                                style="font-size: 14px; font-weight: 600; color: #1434CB; margin-top: 8px;">
                                Visa Card
                              </div>
                            </div>
                          </div>

                          <!-- MasterCard -->
                          <div class="col-lg-3 col-md-6 col-sm-6 col-6" style="padding: 5px;">
                            <div class="payment-card" @click="startDbblMasterCardPayment"
                              style="cursor: pointer; transition: all 0.3s ease; border: 2px solid #e0e0e0; border-radius: 12px; padding: 20px 15px; background: white; box-shadow: 0 2px 8px rgba(0,0,0,0.08); text-align: center; min-height: 140px; display: flex; flex-direction: column; justify-content: center; align-items: center;">
                              <div class="payment-card-logo" style="margin-bottom: 10px;">
                                <img alt="MasterCard" style="height: 60px; max-width: 100%; object-fit: contain;"
                                  class="img-fluid" src="../../theme-assets/images/logo/mastercard-sq.png">
                              </div>
                              <div class="payment-card-title"
                                style="font-size: 14px; font-weight: 600; color: #EB001B; margin-top: 8px;">
                                MasterCard
                              </div>
                            </div>
                          </div>

                        </div>

                        <p
                          style="text-align: center; margin-top: 20px; color: #6c757d; font-size: 13px; line-height: 1.5;">
                          Click on your preferred payment method to proceed securely
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="this.applicationStatus.submitted && this.paymentInfo.status === 'due' && isPayButtonLoading"
            style="max-width: 100%;margin:auto;">
            <button style="margin-top:10px" :class="isPayButtonLoadingClass" type="button" blockx disabled>
              <i class="fa fa-spinner fa-spin"></i> Connecting to the payment gateway ...
            </button>
          </div>
        </div>





      </div>

      <!-- Application Status Tab -->
      <div v-if="activeTab === 'status'" class="tab-content active">
        <div class="content-card">
          <h2 class="section-title">
            <i class="fa fa-line-chart"></i>
            Application Status
          </h2>

          <div class="status-timeline">
            <!-- Step 1: Application Submitted -->
            <div class="status-step" :class="{ completed: applicationStatus.submitted }">
              <div class="status-icon">
                <i class="fa fa-check" v-if="applicationStatus.submitted"></i>
                <i class="fa fa-clock-o" v-else></i>
              </div>
              <div class="status-content">
                <h3>Application Submitted</h3>
                <p v-if="applicationStatus.submitted">
                  Submitted on {{ formatDate(applicationStatus.submittedDate) }}
                </p>
                <p v-else class="text-muted">Pending submission</p>
              </div>
            </div>

            <!-- Step 2: Payment Status -->
            <div class="status-step" :class="{ completed: paymentInfo.status === 'paid' }">
              <div class="status-icon">
                <i class="fa fa-check" v-if="paymentInfo.status === 'paid'"></i>
                <i class="fa fa-clock-o" v-else></i>
              </div>
              <div class="status-content">
                <h3>Payment Status</h3>
                <div v-if="applicationStatus.submitted">
                  <p v-if="paymentInfo.status === 'paid'" class="text-success">
                    <strong>Payment Complete</strong> - Bill #{{ paymentInfo.billNumber }}
                  </p>
                  <p v-else class="text-danger">
                    <strong>Payment Pending</strong> - Bill #{{ paymentInfo.billNumber }} | Amount: ৳{{
                      paymentInfo.amount }}
                  </p>
                </div>
                <p v-else class="text-muted">Awaiting application submission</p>
              </div>
            </div>

            <!-- Step 3: Eligibility Review -->
            <div class="status-step" :class="getStatusClass(applicationStatus.eligibilityStatus)">
              <div class="status-icon">
                <i class="fa fa-check" v-if="applicationStatus.eligibilityStatus === 'approved'"></i>
                <i class="fa fa-times" v-else-if="applicationStatus.eligibilityStatus === 'rejected'"></i>
                <i class="fa fa-clock-o" v-else></i>
              </div>
              <div class="status-content">
                <h3>Eligibility Review</h3>
                <p v-if="applicationStatus.eligibilityStatus === 'approved'" class="text-success">
                  <strong>Approved</strong> - Congratulations! Your application meets the eligibility criteria.
                </p>
                <p v-else-if="applicationStatus.eligibilityStatus === 'rejected'" class="text-danger">
                  <strong>Rejected</strong> - {{ applicationStatus.rejectionReason || 'Please contact admin for details'
                  }}
                </p>
                <p v-else-if="paymentInfo.status === 'paid'" class="text-warning">
                  <strong>Under Review</strong> - Your application is being reviewed by the admin.
                </p>
                <p v-else class="text-muted">Awaiting payment completion.</p>
              </div>
            </div>

            <!-- Step 4: Registration -->
            <div class="status-step" v-if="applicationStatus.eligibilityStatus !== 'rejected'"
              :class="{ completed: applicationStatus.rollNumber }">
              <div class="status-icon">
                <i class="fa fa-check" v-if="applicationStatus.rollNumber"></i>
                <i class="fa fa-clock-o" v-else></i>
              </div>

              <div class="status-content">
                <h3>Registration as Admission Candidate</h3>

                <div v-if="applicationStatus.eligibilityStatus === 'approved' && applicationStatus.rollNumber">
                  <p class="text-success">
                    <strong>Proceed to Complete Registration</strong>
                  </p>

                  <div class="roll-number-box">
                    <label>Your Equivalent ID/Roll Number:</label>
                    <div class="roll-number">{{ applicationStatus.rollNumber }}</div>
                  </div>

                  <p class="text-info"><small>Please save this Equivalent ID /Roll number for Registration.</small></p>
                  <div class="next-steps" style="margin-top: 20px;">
                    <h3 class="subsection-title" style="color: #10b981; font-weight: 600; margin-bottom: 12px;">Next Steps to Complete Your Registration</h3>
                    
                    <ol class="guideline-list" style="padding-left: 30px; margin-top: 8px; line-height: 1.8;">
                      <li style="margin-bottom: 10px; color: #334155;">Click on the <strong style="color: #135acf;">Register</strong> button on the <span style="color: #4F46E5;">Admission Portal.</span></li>
                      <li style="margin-bottom: 10px; color: #334155;">For both <strong style="color: #0a2c57;">HSC Board</strong> and <strong style="color: #0a2c57;">SSC Board</strong>, select <strong style="color: #003e8c;">"Other"</strong>.</li>
                      <li style="margin-bottom: 10px; color: #334155;">Use your <strong style="color: #0a2c57;">Equivalent ID</strong> as both your HSC Roll and SSC Roll.</li>
                      <li style="margin-bottom: 10px; color: #334155;">Complete your profile information carefully.</li>
                      <li style="margin-bottom: 10px; color: #334155;">Submit your application for the required unit and pay the applicable fee.</li>
                    </ol>

                    <div style="margin-top: 16px; padding: 12px 16px; background-color: #f1f5f9; border-left: 4px solid #4F46E5; border-radius: 4px;">
                      <p style="margin: 0; color: #475569; font-size: 14px; font-weight: 500;"> Admission Portal Link: <a 
                        href="https://admission.cu.ac.bd" 
                        target="_blank" 
                        rel="noopener" 
                        style="color: #4F46E5; font-weight: 600; font-size: 15px; text-decoration: underline; display: inline-block; margin-top: 6px;"
                      >
                        https://admission.cu.ac.bd
                      </a>
                      </p>
                      
                    </div>
                  </div>
                </div>

                <p v-else-if="applicationStatus.eligibilityStatus === 'approved'" class="text-info">
                  Equivalent ID will be assigned shortly
                </p>

                <p v-else class="text-muted">
                  Awaiting eligibility approval
                </p>
              </div>
            </div>

          </div>

          <!-- Application Summary -->
          <div v-if="applicationStatus.submitted" class="application-summary">
            <h3>Application Summary</h3>
            <div class="summary-grid">
              <div class="summary-item">
                <label>Name:</label>
                <span>{{ formData.name || 'N/A' }}</span>
              </div>
              <div class="summary-item">
                <label>Father's Name:</label>
                <span>{{ formData.fatherName || 'N/A' }}</span>
              </div>
              <div class="summary-item">
                <label>Mother's Name:</label>
                <span>{{ formData.motherName || 'N/A' }}</span>
              </div>
              <div class="summary-item">
                <label>Date of Birth:</label>
                <span>{{ formData.dateOfBirth || 'N/A' }}</span>
              </div>
              <div class="summary-item">
                <label>Gender:</label>
                <span>{{ formData.gender.toUpperCase() || 'N/A' }}</span>
              </div>
              <div class="summary-item">
                <label>O Level Pass Year:</label>
                <span>{{ formData.oLevelPassYear || 'N/A' }}</span>
              </div>
              <div class="summary-item">
                <label>A Level Pass Year:</label>
                <span>{{ formData.aLevelPassYear || 'N/A' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else>
    <Loader />
  </div>
</template>

<script>
import iziToast from 'izitoast'
import Loader from '../Helper/Loader'
import { BKASH_GCE_PAYMENT_GATEWAY, DBBL_GCE_PAYMENT_GATEWAY, GCE_APPLICATION_SUBMIT, GCE_SUBMIT_DOCUMENTS, GET_GCE_APPLICANT_PROFILE, GET_GCE_DATES } from '../../const/api'
import { gce_logout } from '../../utils/Auth'
import { GET, POST } from '../../utils/GCERequest'
import GCEPaymentComponent from './GCEPaymentComponent.vue'
export default {
  name: 'GCEApplicationPortal',
  components: {
    GCEPaymentComponent,
    Loader
  },
  data() {
    return {
      loading: true,
      applicationId: '',
      activeTab: 'guidelines',
      isLoading: true,
      loadingError: null,
      tabs: [
        { id: 'guidelines', label: 'Guidelines', icon: 'fa fa-file-text-o' },
        { id: 'application', label: 'Application Form', icon: 'fa fa-wpforms' },
        { id: 'status', label: 'Application Status', icon: 'fa fa-line-chart' },
      ],
      isPayButtonLoading: false,
      isPayButtonLoadingClass: 'btn btn-primary btn-block',
      applicationDeadline: new Date('2025-12-10'),
      paymentDeadline: new Date('2026-12-18'),
      isAcceptingApplications: true,
      isAcceptingPayments: true,
      isSubmitting: false,
      sscType: '',
      hscType: '',
      formData: {
        name: '',
        fatherName: '',
        motherName: '',
        dateOfBirth: '',
        gender: '',
        oLevelPassYear: '',
        aLevelPassYear: '',
        aLevelSubjects: [],
        oLevelSubjects: []
      },
      uploadedFiles: {
        identityProof: { type: '', file: null },
        aLevelStatementOfEntry: null,
        aLevelTranscript: null,
        aLevelCertificate: null,
        oLevelStatementOfEntry: null,
        oLevelTranscript: null,
        oLevelCertificate: null
      },
      applicationStatus: {
        submitted: false,
        eligibilityStatus: 'pending',
        rollNumber: '',
        submittedDate: '',
        rejectionReason: ''
      },
      paymentInfo: {
        billNumber: '',
        amount: 0,
        status: 'due'
      },
      aLevelSubjects: [
        { value: 'ACCOUNTING', label: 'ACCOUNTING' },
        { value: 'ADDITIONALMATHEMATICS', label: 'ADDITIONAL MATHEMATICS' },
        { value: 'ADDITIONALSCIENCE', label: 'ADDITIONAL SCIENCE' },
        { value: 'AGRICULTURE', label: 'AGRICULTURE' },
        { value: 'APPLIEDICT', label: 'APPLIED ICT' },
        { value: 'ARABIC', label: 'ARABIC' },
        { value: 'ARTANDDESIGN', label: 'ART AND DESIGN' },
        { value: 'ARTCRAFTANDDESIGN', label: 'ART, CRAFT AND DESIGN' },
        { value: 'BANGLADESHESTUDIES', label: 'BANGLADESH STUDIES' },
        { value: 'BENGALI', label: 'BENGALI' },
        { value: 'BIOLOGY', label: 'BIOLOGY' },
        { value: 'BUSINESSSTUDIES', label: 'BUSINESS STUDIES' },
        { value: 'CHEMISTRY', label: 'CHEMISTRY' },
        { value: 'COMMERCE', label: 'COMMERCE' },
        { value: 'COMMERCIALAPPLICATIONS', label: 'COMMERCIAL APPLICATIONS' },
        { value: 'COMPUTERSCIENCE', label: 'COMPUTER SCIENCE' },
        { value: 'COREMATHEMATICS', label: 'CORE MATHEMATICS' },
        { value: 'DECISIONMATHEMATICS', label: 'DECISION MATHEMATICS' },
        { value: 'DESIGNANDCOMMUNICATION', label: 'DESIGN AND COMMUNICATION' },
        { value: 'DESIGNANDTECHNOLOGY', label: 'DESIGN AND TECHNOLOGY' },
        { value: 'DRAMAANDTHEATRESTUDIES', label: 'DRAMA AND THEATRE STUDIES' },
        { value: 'ECONOMICS', label: 'ECONOMICS' },
        { value: 'ENGLISH', label: 'ENGLISH' },
        { value: 'ENGLISHCOMM', label: 'ENGLISH COMM' },
        { value: 'ENGLISHLANGUAGE', label: 'ENGLISH LANGUAGE' },
        { value: 'ENGLISHLITERATURE', label: 'ENGLISH LITERATURE' },
        { value: 'ENGLISHLANGUAGEANDLITERATURE', label: 'ENGLISH LANGUAGE AND LITERATURE' },
        { value: 'ENVIRONMENTALMANAGEMENT', label: 'ENVIRONMENTAL MANAGEMENT' },
        { value: 'FASHIONANDTEXTILES', label: 'FASHION AND TEXTILES' },
        { value: 'FINEART', label: 'FINE ART' },
        { value: 'FOODANDNUTRITION', label: 'FOOD AND NUTRITION' },
        { value: 'FRENCH', label: 'FRENCH' },
        { value: 'FURTHERPUREMATHEMATICS', label: 'FURTHER PURE MATHEMATICS' },
        { value: 'GEOGRAPHY', label: 'GEOGRAPHY' },
        { value: 'GERMANLANGUAGE', label: 'GERMAN LANGUAGE' },
        { value: 'GOVERNMENTANDPOLITICS', label: 'GOVERNMENT AND POLITICS' },
        { value: 'HINDI', label: 'HINDI COURSE-B' },
        { value: 'HINDUISM', label: 'HINDUISM' },
        { value: 'HISTORY', label: 'HISTORY' },
        { value: 'HISTORYANDCIVICS', label: 'HISTORY AND CIVICS' },
        { value: 'HUMANANDSOCIALBIOLOGY', label: 'HUMAN AND SOCIAL BIOLOGY' },
        { value: 'HUMANBIOLOGY', label: 'HUMAN BIOLOGY' },
        { value: 'HEALTHANDPHYSICALEDUCATION', label: 'HEALTH AND PHYSICAL EDUCATION' },
        { value: 'ICT', label: 'ICT' },
        { value: 'ISLAMICSTUDIES', label: 'ISLAMIC STUDIES' },
        { value: 'ISLAMIYAT', label: 'ISLAMIYAT' },
        { value: 'LAW', label: 'LAW' },
        { value: 'LIFESCIENCE', label: 'LIFE SCIENCE' },
        { value: 'MARINESCIENCE', label: 'MARINE SCIENCE' },
        { value: 'MATHEMATICS', label: 'MATHEMATICS' },
        { value: 'MATHEMATICSSPECIFICATIONA', label: 'MATHEMATICS SPECIFICATION A' },
        { value: 'MATHEMATICSSPECIFICATIONB', label: 'MATHEMATICS SPECIFICATION B' },
        { value: 'MECHANICS', label: 'MECHANICS' },
        { value: 'MUSIC', label: 'MUSIC' },
        { value: 'PHYSICALEDUCATION', label: 'PHYSICAL EDUCATION' },
        { value: 'PHYSICS', label: 'PHYSICS' },
        { value: 'PSYCHOLOGY', label: 'PSYCHOLOGY' },
        { value: 'RELIGIOUSSTUDIES', label: 'RELIGIOUS STUDIES' },
        { value: 'SCIENCE', label: 'SCIENCE' },
        { value: 'SOCIALSCIENCE', label: 'SOCIAL SCIENCE' },
        { value: 'SOCIOLOGY', label: 'SOCIOLOGY' },
        { value: 'STATISTICS', label: 'STATISTICS' },
        { value: 'TRAVELANDTOURISM', label: 'TRAVEL AND TOURISM' },
        { value: 'TURKISH', label: 'TURKISH' },
        { value: 'URDU', label: 'URDU' },
        { value: 'SPANISH', label: 'SPANISH' },
        { value: 'HOMESCIENCE', label: 'HOME SCIENCE' },
        { value: 'MULTIMEDIA', label: 'MULTIMEDIA' },
        { value: 'WORKEXPERIENCE', label: 'WORK EXPERIENCE' }
      ],
      oLevelSubjects: [
        { value: 'ACCOUNTING', label: 'ACCOUNTING' },
        { value: 'ADDITIONALMATHEMATICS', label: 'ADDITIONAL MATHEMATICS' },
        { value: 'ADDITIONALSCIENCE', label: 'ADDITIONAL SCIENCE' },
        { value: 'AGRICULTURE', label: 'AGRICULTURE' },
        { value: 'APPLIEDICT', label: 'APPLIED ICT' },
        { value: 'ARABIC', label: 'ARABIC' },
        { value: 'ARTANDDESIGN', label: 'ART AND DESIGN' },
        { value: 'ARTCRAFTANDDESIGN', label: 'ART, CRAFT AND DESIGN' },
        { value: 'BANGLADESHESTUDIES', label: 'BANGLADESH STUDIES' },
        { value: 'BENGALI', label: 'BENGALI' },
        { value: 'BIOLOGY', label: 'BIOLOGY' },
        { value: 'BUSINESSSTUDIES', label: 'BUSINESS STUDIES' },
        { value: 'CHEMISTRY', label: 'CHEMISTRY' },
        { value: 'COMMERCE', label: 'COMMERCE' },
        { value: 'COMMERCIALAPPLICATIONS', label: 'COMMERTIAL APPLICATIONS' },
        { value: 'COMPUTERSCIENCE', label: 'COMPUTER SCIENCE' },
        { value: 'COREMATHEMATICS', label: 'CORE MATHEMATICS' },
        { value: 'DECISIONMATHEMATICS', label: 'DECISION MATHEMATICS' },
        { value: 'DESIGNANDCOMMUNICATION', label: 'DESIGN AND COMMUNICATION' },
        { value: 'DESIGNANDTECHNOLOGY', label: 'DESIGN AND TECHNOLOGY' },
        { value: 'DRAMAANDTHEATRESTUDIES', label: 'DRAMA AND THEATRE STUDIES' },
        { value: 'ECONOMICS', label: 'ECONOMICS' },
        { value: 'ENGLISH', label: 'ENGLISH' },
        { value: 'ENGLISHCOMM', label: 'ENGLISH COMM' },
        { value: 'ENGLISHLANGUAGE', label: 'ENGLISH LANGUAGE' },
        { value: 'ENGLISHLITERATURE', label: 'ENGLISH LITERATURE' },
        { value: 'ENGLISHLANGUAGEANDLITERATURE', label: 'ENGLISH LANGUAGE AND LITERATURE' },
        { value: 'ENVIRONMENTALMANAGEMENT', label: 'ENVIRONMENTAL MANAGEMENT' },
        { value: 'FASHIONANDTEXTILES', label: 'FASHION AND TEXTILES' },
        { value: 'FINEART', label: 'FINE ART' },
        { value: 'FOODANDNUTRITION', label: 'FOOR AND NUTRITION' },
        { value: 'FRENCH', label: 'FRENCH' },
        { value: 'FURTHERPUREMATHEMATICS', label: 'FURTHER PURE MATHEMATICS' },
        { value: 'GEOGRAPHY', label: 'GEOGRAPHY' },
        { value: 'GERMANLANGUAGE', label: 'GERMAN LANGUAGE' },
        { value: 'GOVERNMENTANDPOLITICS', label: 'GOVERNMENT AND POLITICS' },
        { value: 'HEALTHANDPHYSICALEDUCATION', label: 'HEALTH AND PHYSICAL EDUCATION' },
        { value: 'HINDI', label: 'HINDI COURSE-B' },
        { value: 'HINDUISM', label: 'HINDUISM' },
        { value: 'HISTORY', label: 'HISTORY' },
        { value: 'HISTORYANDCIVICS', label: 'HISTORY AND CIVICS' },
        { value: 'HUMANANDSOCIALBIOLOGY', label: 'HUMAN AND SOCIAL BIOLOGY' },
        { value: 'HUMANBIOLOGY', label: 'HUMAN BIOLOGY' },
        { value: 'ICT', label: 'ICT' },
        { value: 'ISLAMICSTUDIES', label: 'ISLAMIC STUDIES' },
        { value: 'ISLAMIYAT', label: 'ISLAMIYAT' },
        { value: 'LAW', label: 'LAW' },
        { value: 'LIFESCIENCE', label: 'LIFE SCIENCE' },
        { value: 'MARINESCIENCE', label: 'MARINE SCIENCE' },
        { value: 'MATHEMATICS', label: 'MATHEMATICS' },
        { value: 'MATHEMATICSSPECIFICATIONA', label: 'MATHEMATICS SPECIFICATION A' },
        { value: 'MATHEMATICSSPECIFICATIONB', label: 'MATHEMATICS SPECIFICATION B' },
        { value: 'MECHANICS', label: 'MECHANICS' },
        { value: 'MUSIC', label: 'MUSIC' },
        { value: 'PHYSICALEDUCATION', label: 'PHYSICAL EDUCATION' },
        { value: 'PHYSICS', label: 'PHYSICS' },
        { value: 'PSYCHOLOGY', label: 'PSYCHOLOGY' },
        { value: 'RELIGIOUSSTUDIES', label: 'RELIGIOUS STUDIES' },
        { value: 'SCIENCE', label: 'SCIENCE' },
        { value: 'SOCIALSCIENCE', label: 'SOCIAL SCIENCE' },
        { value: 'SOCIOLOGY', label: 'SOCIOLOGY' },
        { value: 'STATISTICS', label: 'STATISTICS' },
        { value: 'TRAVELANDTOURISM', label: 'TRAVEL AND TOURISM' },
        { value: 'TURKISH', label: 'TURKISH' },
        { value: 'URDU', label: 'URDU' },
        { value: 'SPANISH', label: 'SPANISH' },
        { value: 'HOMESCIENCE', label: 'HOME SCIENCE' },
        { value: 'MULTIMEDIA', label: 'MULTEMEDIA' },
        { value: 'WORKEXPERIENCE', label: 'WORK EXPERIENCE' }
      ],
      gradePoints: {
        'A*': 5,   // Only for A Level if needed
        'A': 4,
        'B': 3,
        'C': 2,
        'D': 1,
        'F': 0   // Only for O Level, optional: assign -1 or 0
      }
    }
  },
  computed: {
    isDeadlinePassed() {
      return new Date() > this.applicationDeadline;
    },
    isPaymentDeadlinePassed() {
      return new Date() > this.paymentDeadline;
    },
    gceToken() {
      return this.$root.$store.getters.getGceToken;
    },
    gceUser() {
      return this.$root.$store.getters.getGceUser;
    },
    visibleTabs() {
      if (this.applicationStatus.submitted && this.paymentInfo.status !== 'paid') {
        const paymentTab = {
          id: 'payment',
          label: 'Payment',
          icon: 'fa fa-credit-card'
        };

        // Insert at index 2
        const newTabs = [...this.tabs];
        newTabs.splice(2, 0, paymentTab);

        return newTabs;
      }

      return this.tabs;
    }
  },
  async mounted() {
    await this.getGCEDates()
    await this.fetchApplicantDetails()
  },
  methods: {
    async getGCEDates() {
      try {
        const response = await GET(GET_GCE_DATES);
        const applicationItem = response.data.find(item => item.key === "GCEApply");
        if (applicationItem) {
          const endDate = new Date(applicationItem.closeDate);
          this.applicationDeadline = endDate;
          this.isAcceptingApplications = new Date() <= endDate;
        }
        const paymentItem = response.data.find(item => item.key === "GCEPayment");
        if (paymentItem) {
          const paymentEndDate = new Date(paymentItem.closeDate)
          this.paymentDeadline = paymentEndDate
          this.isAcceptingPayments = new Date() <= paymentEndDate
        }
      } catch (error) {
        console.error('Error fetching GCE dates:', error);
      } finally {
        this.loading = false
      }
    },
    async fetchApplicantDetails() {
      try {
        this.isLoading = true
        this.loadingError = null
        const token = this.gceToken


        if (!token) {
          // No token found, redirect to login
          this.$router.push('/gce/login');
          return
        }

        const appId = (this.gceUser || {}).application_id || this.$route.params.applicationId;
        // this.applicationId = appId;

        if (!appId) {
          this.loadingError = 'Application ID not found'
          this.$router.push('/gce/login')
          return;
        }
        // Make API call to fetch applicant details
        const apiURL = `${GET_GCE_APPLICANT_PROFILE}/${appId}`
        const response = await GET(apiURL)
        // console.log(response)

        // Handle authentication errors
        if (response.status === 401 || response.status === 403) {
          await this.$root.$store.dispatch('UPDATE_GCE_AUTH_RESET');
          this.$router.push('/gce/login');
          return;
        }

        if (response.status !== 200) {
          // throw new Error(`Failed to fetch applicant details: ${response.statusText}`);
          // console.log(response.statusText);
        }

        const result = response.data

        if (result) {
          await this.populateFormData(result)
        } else {
          // throw new Error('Invalid response format');
          iziToast.error({
            title: 'Error',
            message: 'Invalid response format',

          })

        }

      } catch (error) {
        console.error('Error fetching applicant details:', error);
        this.loadingError = error.message || 'Failed to load applicant details';

        // If it's a network error or server error, show error message
        // Otherwise redirect to login
        if (error.message.includes('401') || error.message.includes('403')) {
          this.$router.push('/gce/login')
        }
      } finally {
        this.isLoading = false
        this.loading = false
      }
    },

    async populateFormData(data) {
      // Populate application ID
      this.applicationId = data.application_id || '';
      this.sscType = data.ssc_type || '';
      this.hscType = data.hsc_type || '';

      // Populate personal information
      this.formData.name = data.name || '';
      this.formData.fatherName = data.father_name || '';
      this.formData.motherName = data.mother_name || '';
      this.formData.dateOfBirth = data.date_of_birth || '';
      this.formData.gender = data.gender || '';

      // Populate education information
      this.formData.oLevelPassYear = data.o_level_pass_year || '';
      this.formData.aLevelPassYear = data.a_level_pass_year || '';


      // Populate subjects (convert from array to object format if needed)
      if (data.a_level_subjects && Array.isArray(data.a_level_subjects)) {
        this.formData.aLevelSubjects = JSON.parse(data.a_level_subjects[0]).map((subject, index) => {
          const s = subject || {};
          const name = (typeof s === 'object') ? (s.value || s.name || s.label || '') : s;
          const grade = (typeof s === 'object') ? (s.grade || s.grade_letter || s.customGrade || '') : '';
          const gradePoint = (typeof s === 'object') ? (s.gradePoint || s.grade_point || s.grade_point_value || '') : '';
          return {
            id: Date.now() + index,
            name: name || '',
            customName: (name === 'OTHER' && s.customName) ? s.customName : '',
            grade: grade || '',
            customGrade: (grade === 'OTHER' && s.customGrade) ? s.customGrade : '',
            gradePoint: gradePoint || ''
          };
        });
      }

      if (data.o_level_subjects && Array.isArray(data.o_level_subjects)) {
        this.formData.oLevelSubjects = JSON.parse(data.o_level_subjects[0]).map((subject, index) => {
          const s = subject || {};
          const name = (typeof s === 'object') ? (s.value || s.name || s.label || '') : s;
          const grade = (typeof s === 'object') ? (s.grade || s.grade_letter || s.customGrade || '') : '';
          const gradePoint = (typeof s === 'object') ? (s.gradePoint || s.grade_point || s.grade_point_value || '') : '';
          return {
            id: Date.now() + index + 1000,
            name: name || '',
            customName: (name === 'OTHER' && s.customName) ? s.customName : '',
            grade: grade || '',
            customGrade: (grade === 'OTHER' && s.customGrade) ? s.customGrade : '',
            gradePoint: gradePoint || ''
          };
        });
      }

      // Populate identity proof type
      if (data.identity_proof_type) {
        this.uploadedFiles.identityProof.type = data.identity_proof_type;
      }

      // Populate application status
      this.applicationStatus.submitted = data.application_submitted || false;
      this.applicationStatus.eligibilityStatus = data.eligibility_status || 'pending';
      this.applicationStatus.rollNumber = data.roll_number || '';
      this.applicationStatus.submittedDate = data.submitted_date || '';
      this.applicationStatus.rejectionReason = data.rejection_reason || '';

      // Populate payment information
      this.paymentInfo.billNumber = data.bill_number || '';
      this.paymentInfo.amount = data.amount || 0;
      // data.payment_status can be assigned/due which means due or paid
      this.paymentInfo.status = data.payment_status || 'due';

      // console.log(data)

      // If application is already submitted, show status tab
      if (this.applicationStatus.submitted) {
        if (this.paymentInfo.status === 'paid') {
          this.activeTab = 'status'
        } else {
          this.activeTab = 'payment'
        }
      }
    },

    async handleLogout() {
      try {
        await gce_logout()
        // Clear Vuex store
        await this.$root.$store.dispatch('UPDATE_GCE_AUTH_RESET')
        // Redirect to login
        this.$router.push('/gce/login')
      } catch (error) {
        console.error('Logout error:', error)
        // Even if logout fails, clear store and redirect to login
        await this.$root.$store.dispatch('UPDATE_GCE_AUTH_RESET')
        this.$router.push('/gce/login')
      }
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    addSubject(type) {
      this.formData[type].push({
        id: Date.now(),
        name: '',
        customName: '',
        grade: '',
        customGrade: '',
        gradePoint: ''
      });
    },

    removeSubject(type, id) {
      this.formData[type] = this.formData[type].filter(sub => sub.id !== id);
    },

    updateGradePoint(type, id) {
      const subject = this.formData[type].find(sub => sub.id === id);
      if (!subject) return
      // If user chose 'OTHER' as grade, allow manual gradePoint entry and do not overwrite it
      if (subject.grade === 'OTHER') {
        return;
      }

      // If user selected a predefined grade, compute grade point
      if (subject.grade) {
        subject.gradePoint = this.gradePoints.hasOwnProperty(subject.grade) ? this.gradePoints[subject.grade] : '';
        return;
      }

      // If there's a customGrade entered while grade isn't set to OTHER, try to map it
      if (subject.customGrade) {
        subject.gradePoint = this.gradePoints.hasOwnProperty(subject.customGrade) ? this.gradePoints[subject.customGrade] : (subject.gradePoint || '');
        return;
      }

      // No grade information
      subject.gradePoint = '';
    },

    handleFileUpload(event, type) {
      const file = event.target.files[0];
      if (!file) return;

      if (file.type !== 'application/pdf') {
        alert(`File ${file.name} is not a PDF. Only PDF files are accepted.`);
        event.target.value = '';
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert(`File ${file.name} exceeds 5 MB limit`);
        event.target.value = '';
        return;
      }

      if (type === 'identityProof') {
        this.uploadedFiles.identityProof.file = file;
      } else {
        this.uploadedFiles[type] = file;
      }
    },

    async handleSubmit() {
      if (!this.isAcceptingApplications) {
        iziToast.error({
          title: 'Error',
          message: 'Application deadline has passed. Submissions are no longer accepted.',

        });
        return;
      }

      // Validation
      if (!this.validateForm()) {
        return;
      }

      this.isSubmitting = true;

      try {
        // Step 1: Upload all documents
        const documentsUploaded = await this.uploadAllDocuments();

        if (!documentsUploaded) {
          throw new Error('Document upload failed');
        }

        // Step 2: Submit application with form data
        const submissionResponse = await this.submitApplicationData();

        if (submissionResponse.success) {
          // Update application status
          this.applicationStatus = {
            submitted: true,
            eligibilityStatus: submissionResponse.data.eligibility_status || 'pending',
            rollNumber: submissionResponse.data.roll_number || '',
            submittedDate: submissionResponse.data.submitted_date,
            rejectionReason: ''
          };

          // Update payment info from response
          this.paymentInfo = {
            billNumber: submissionResponse.data.payment.bill_number,
            amount: submissionResponse.data.payment.amount,
            status: submissionResponse.data.payment.status
          };

          iziToast.success({
            title: 'Success',
            message: 'Application submitted successfully!',

          });

          // Switch to status tab
          this.activeTab = 'payment';

          // Optional: Redirect to payment page after a delay
          setTimeout(() => {
            // reload
            window.location.reload();
          }, 1000);
        }
      } catch (error) {
        console.error('Submission error:', error);
        iziToast.error({
          title: 'Error',
          message: error.message || 'Failed to submit application. Please try again.',

        });
      } finally {
        this.isSubmitting = false;
      }
    },

    validateForm() {
      // Personal information validation
      if (!this.formData.name || !this.formData.fatherName || !this.formData.motherName) {
        iziToast.error({
          title: 'Error',
          message: 'Please fill all required personal information fields',

        });
        return false;
      }

      if (!this.formData.dateOfBirth || !this.formData.gender) {
        iziToast.error({
          title: 'Error',
          message: 'Please provide date of birth and gender',

        });
        return false;
      }

      // Subject validation
      // A Level: if applicable (hscType === 'ALV') ensure at least one and each has name+grade (support 'Other')
      if (this.hscType === 'ALV') {

        for (const subject of this.formData.aLevelSubjects) {
          const effectiveName = subject.name === 'OTHER' ? (subject.customName || '') : (subject.name || '');
          const effectiveGrade = subject.grade === 'OTHER' ? (subject.customGrade || '') : (subject.grade || '');
          if (!effectiveName || !effectiveGrade) {
            iziToast.error({
              title: 'Error',
              message: 'All A Level subjects must have a subject name and grade',
            });
            return false;
          }

          // If grade is OTHER, require manual grade point
          if (subject.grade === 'OTHER' && (subject.gradePoint === '' || subject.gradePoint === null || subject.gradePoint === undefined)) {
            iziToast.error({
              title: 'Error',
              message: 'Please enter grade point for A Level subject with custom grade',
            });
            return false;
          }
        }
      }

      // O Level: if applicable (sscType === 'OLV') ensure at least one and each has name+grade (support 'Other')
      if (this.sscType === 'OLV') {

        for (const subject of this.formData.oLevelSubjects) {
          const effectiveName = subject.name === 'OTHER' ? (subject.customName || '') : (subject.name || '');
          const effectiveGrade = subject.grade === 'OTHER' ? (subject.customGrade || '') : (subject.grade || '');
          if (!effectiveName || !effectiveGrade) {
            iziToast.error({
              title: 'Error',
              message: 'All O Level subjects must have a subject name and grade',
            });
            return false;
          }

          // If grade is OTHER, require manual grade point
          if (subject.grade === 'OTHER' && (subject.gradePoint === '' || subject.gradePoint === null || subject.gradePoint === undefined)) {
            iziToast.error({
              title: 'Error',
              message: 'Please enter grade point for O Level subject with custom grade',
            });
            return false;
          }
        }
      }

      // Document validation
      if (!this.uploadedFiles.identityProof.type || !this.uploadedFiles.identityProof.file) {
        iziToast.error({
          title: 'Error',
          message: 'Please select identity proof type and upload the document',
        });
        return false;
      }

      if (!this.uploadedFiles.aLevelStatementOfEntry || !this.uploadedFiles.aLevelTranscript) {
        iziToast.error({
          title: 'Error',
          message: 'Please upload all A Level documents',
        });
        return false;
      }

      if (!this.uploadedFiles.oLevelStatementOfEntry || !this.uploadedFiles.oLevelTranscript || !this.uploadedFiles.oLevelCertificate) {
        iziToast.error({
          title: 'Error',
          message: 'Please upload all O Level documents',
        });
        return false;
      }

      return true;
    },

    async uploadAllDocuments() {
      try {
        const formData = new FormData();

        // Add all document files to FormData
        if (this.uploadedFiles.identityProof.file) {
          formData.append('identity_proof_file', this.uploadedFiles.identityProof.file);
        }
        if (this.uploadedFiles.aLevelStatementOfEntry) {
          formData.append('a_level_statement_of_entry', this.uploadedFiles.aLevelStatementOfEntry);
        }
        if (this.uploadedFiles.aLevelTranscript) {
          formData.append('a_level_transcript', this.uploadedFiles.aLevelTranscript);
        }
        if (this.uploadedFiles.aLevelCertificate) {
          formData.append('a_level_certificate', this.uploadedFiles.aLevelCertificate);
        }
        if (this.uploadedFiles.oLevelStatementOfEntry) {
          formData.append('o_level_statement_of_entry', this.uploadedFiles.oLevelStatementOfEntry);
        }
        if (this.uploadedFiles.oLevelTranscript) {
          formData.append('o_level_transcript', this.uploadedFiles.oLevelTranscript);
        }
        if (this.uploadedFiles.oLevelCertificate) {
          formData.append('o_level_certificate', this.uploadedFiles.oLevelCertificate);
        }
        // console.log(`${process.env.VUE_APP_API_URL}/api/gce/submit-documents/${this.applicationId}`)
        const response = await POST(`${GCE_SUBMIT_DOCUMENTS}${this.applicationId}`, formData);


        if (response.data.success) {
          iziToast.success({
            title: 'Documents Uploaded',
            message: `${response.data.data.uploaded.length} document(s) uploaded successfully`,

          });
          return true;
        } else {
          // throw new Error(response.data.message || 'Document upload failed');
        }
      } catch (error) {
        console.error('Document upload error:', error);
        iziToast.error({
          title: 'Upload Error',
          message: error.details || 'Failed to upload documents',

        })
        return false
      }
    },

    async submitApplicationData() {
      try {
        // Normalize subjects: replace 'OTHER' entries with custom values before sending
        const normalizeSubjects = (arr) => {
          return arr.map(s => ({
            name: s.name === 'OTHER' ? (s.customName || '') : s.name,
            grade: s.grade === 'OTHER' ? (s.customGrade || '') : s.grade,
            gradePoint: s.gradePoint || ''
          }))
        }

        const payload = {
          name: this.formData.name,
          father_name: this.formData.fatherName,
          mother_name: this.formData.motherName,
          date_of_birth: this.formData.dateOfBirth,
          gender: this.formData.gender,
          o_level_pass_year: this.formData.oLevelPassYear,
          a_level_pass_year: this.formData.aLevelPassYear,
          identity_proof_type: this.uploadedFiles.identityProof.type,
          a_level_subjects: JSON.stringify(normalizeSubjects(this.formData.aLevelSubjects)),
          o_level_subjects: JSON.stringify(normalizeSubjects(this.formData.oLevelSubjects))
        };

        const response = await POST(`${GCE_APPLICATION_SUBMIT}${this.applicationId}`, payload);

        if (response.data.success) {
          return {
            success: true,
            data: response.data.data
          };
        } else {
          throw new Error(response.data.message || 'Submission failed');
        }
      } catch (error) {
        console.error('Submission error:', error);
        throw new error.message || 'Failed to submit application';
      }
    },

    getStatusClass(status) {
      if (status === 'approved') return 'completed';
      if (status === 'rejected') return 'rejected';
      return '';
    },


    /*Get Application by Unit*/
    async startBkashCheckoutService() {
      if (!this.isAcceptingPayments) {
        iziToast.error({ message: 'Payment deadline has passed.' });
        return;
      }

      try {
        this.isPayButtonLoading = true;

        let data = await GET(BKASH_GCE_PAYMENT_GATEWAY + '?bill=' + this.paymentInfo.billNumber);

        if (data.status == 200) {
          let response = data.data;

          // console.log('Processing ... ', response);

          if (response.type == 'success' && response.code == "bkashURL")
            window.location.href = response.msg;
          /*this.goForUrl('admission.sust.edu.bd');*/
          else {
            if (response.type == 'success')
              this.$iziToast.success({ message: response.msg });
            else {
              if (response.type == 'error')
                this.$iziToast.error({ message: response.msg });
              else {
                if (response.type == 'info')
                  this.$iziToast.info({ message: response.msg });
                else {
                  if (response.type == 'danger')
                    this.$iziToast.danger({ message: response.msg });
                }
              }
            }
          }
        }
        else
          this.$iziToast.error({ message: 'Unable to connect the payment gateway. contact with Helpdesk.' });
      } catch (err) {
        this.isPayButtonLoading = false;
      } finally {
        this.isPayButtonLoading = false;
      };
    },

    /**
     * Start DBBL Rocket Payment (Modern Spring Boot API Integration)
     * Rocket uses card_type = 6
     */
    async startDbblRocketPayment() {
      this.startDbblPayment(6);
    },

    /**
     * Start DBBL Nexus Payment (Visa/MasterCard)
     * Nexus uses card_type = 4
     * @deprecated Use startDbblVisaPayment or startDbblMasterCardPayment instead
     */
    async startDbblNexsusPayment() {
      this.startDbblPayment(4);
    },

    /**
     * Start DBBL Visa Payment
     * Visa uses card_type = 4
     */
    async startDbblVisaPayment() {
      this.startDbblPayment(4);
    },

    /**
     * Start DBBL MasterCard Payment
     * MasterCard uses card_type = 5
     */
    async startDbblMasterCardPayment() {
      this.startDbblPayment(5);
    },

    /**
     * Common DBBL payment initiation method
     * @param {number} card_type - 4 for Visa, 5 for MasterCard, 6 for Rocket
     */
    async startDbblPayment(card_type = 6) {
      if (!this.isAcceptingPayments) {
        iziToast.error({ message: 'Payment deadline has passed.' });
        return;
      }
      try {
        this.isPayButtonLoading = true;

        // Determine payment method name based on card type
        let paymentMethod = 'DBBL';
        if (card_type == 4) {
          paymentMethod = 'DBBL Visa';
        } else if (card_type == 5) {
          paymentMethod = 'DBBL MasterCard';
        } else if (card_type == 6) {
          paymentMethod = 'DBBL Rocket';
        }

        // console.log(`Initiating ${paymentMethod} payment with card_type=${card_type}`);

        let data = await GET(DBBL_GCE_PAYMENT_GATEWAY + '?bill=' + this.paymentInfo.billNumber + '&card_type=' + card_type);

        if (data.status == 200) {
          let response = data.data;

          // console.log(`${paymentMethod} Processing ... `, response);

          if (response.success && response.type == 'success' && response.code == "dbblGatewayURL") {
            // Redirect to DBBL gateway
            window.location.href = response.msg;
          } else {
            // Show appropriate toast message
            if (response.type == 'success') {
              this.$iziToast.success({ message: response.msg });
            } else if (response.type == 'error') {
              this.$iziToast.error({ message: response.msg });
            } else if (response.type == 'info') {
              this.$iziToast.info({ message: response.msg });
            } else if (response.type == 'warning') {
              this.$iziToast.warning({ message: response.msg });
            } else {
              this.$iziToast.error({ message: response.msg || 'Payment initialization failed' });
            }
          }
        } else {
          this.$iziToast.error({ message: 'Unable to connect to DBBL payment gateway. Please contact Helpdesk.' });
        }
      } catch (err) {
        console.error('DBBL Payment Error:', err);
        this.$iziToast.error({ message: 'An error occurred while initiating payment. Please try again.' });
      } finally {
        this.isPayButtonLoading = false;
      }
    },
  }
}

</script>

<style scoped>
/* Global Styles */
.admission-portal {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e3e9f2 100%);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* Header */
.portal-header {
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 1000;
  padding: 1.2rem 0;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.app-info {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 16px;
}

.app-label {
  color: #6b7280;
  font-weight: 500;
}

.app-id {
  font-weight: 700;
  color: #4F46E5;
  font-size: 18px;
}

.btn-logout {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 22px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-logout:hover {
  background: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

/* Tab Navigation */
.tab-navigation {
  background: white;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.tab-nav-wrapper {
  display: flex;
  justify-content: center;
  overflow-x: auto;
  padding: 0 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 32px;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  color: #6b7280;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.tab-btn:hover {
  color: #4F46E5;
  background: #f9fafb;
}

.tab-btn.active {
  color: #4F46E5;
  border-bottom-color: #4F46E5;
  background: #eef2ff;
}

.tab-btn i {
  font-size: 20px;
}

/* Tab Content */
.tab-content-wrapper {
  padding: 35px 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.tab-content {
  display: none;
}

.tab-content.active {
  display: block;
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.content-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 40px;
}

.section-title {
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 28px;
  display: flex;
  align-items: center;
  gap: 14px;
}

.section-title i {
  color: #4F46E5;
  font-size: 36px;
}

/* Payment Status Card */
.payment-status-card {
  background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
  border: 2px solid #c7d2fe;
  border-radius: 10px;
  padding: 24px;
  margin-bottom: 32px;
}

.payment-info {
  display: flex;
  flex-wrap: wrap;
  gap: 32px;
  align-items: center;
}

.payment-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.payment-item label {
  font-size: 15px;
  font-weight: 600;
  color: #4b5563;
  margin: 0;
}

.bill-number {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  background: white;
  padding: 6px 14px;
  border-radius: 6px;
}

.amount {
  font-size: 20px;
  font-weight: 700;
  color: #10b981;
}

.badge {
  padding: 8px 18px;
  border-radius: 20px;
  font-weight: 700;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-success {
  background: #10b981;
  color: white;
}

.badge-danger {
  background: #ef4444;
  color: white;
}

/* Guidelines */
.guideline-content {
  color: #374151;
  font-size: 15px;
}

.alert-box {
  padding: 20px 24px;
  border-radius: 10px;
  margin-bottom: 28px;
  border-left: 4px solid;
}

.alert-box h3 {
  font-size: 17px;
  font-weight: 600;
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.alert-box p {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
}

.alert-info {
  background: #eff6ff;
  border-color: #3b82f6;
  color: #1e40af;
}

.alert-warning {
  background: #fffbeb;
  border-color: #f59e0b;
  color: #92400e;
}

.alert-danger {
  background: #fef2f2;
  border-color: #ef4444;
  color: #991b1b;
}

.guideline-section {
  margin-bottom: 28px;
}

.guideline-section h3 {
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 14px;
}

.guideline-list {
  margin: 0;
  padding-left: 28px;
  line-height: 1.9;
}

.guideline-list li {
  margin-bottom: 10px;
  color: #4b5563;
  font-size: 15px;
}

/* Application Form */
.application-form {
  margin-top: 28px;
}

.form-section {
  margin-bottom: 48px;
}

.form-section-title {
  font-size: 22px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 24px;
  padding-bottom: 14px;
  border-bottom: 2px solid #4F46E5;
}

.form-group {
  margin-bottom: 24px;
}

.modern-label {
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 10px;
}

.required {
  color: #ef4444;
  font-weight: bold;
}

.input-wrapper {
  position: relative;
}

.input-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 18px;
}

.modern-input {
  width: 100%;
  padding: 14px 14px 14px 46px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 15px;
  transition: all 0.2s;
  background: white;
}

.modern-input:focus {
  outline: none;
  border-color: #4F46E5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.modern-input:disabled {
  background: #f9fafb;
  color: #9ca3af;
  cursor: not-allowed;
}

.modern-select {
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 15px;
  transition: all 0.2s;
  background: white;
  cursor: pointer;
}

.modern-select:focus {
  outline: none;
  border-color: #4F46E5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.modern-select:disabled {
  background: #f9fafb;
  color: #9ca3af;
  cursor: not-allowed;
}

/* Subject Table */
.table-responsive {
  overflow-x: auto;
  margin-bottom: 20px;
}

.subject-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
}

.subject-table thead {
  background: #f9fafb;
}

.subject-table th {
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  font-size: 15px;
  border-bottom: 2px solid #e5e7eb;
}

.subject-table td {
  padding: 14px 16px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 15px;
  color: #4b5563;
}

.subject-table tbody tr:hover {
  background: #f9fafb;
}

.table-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 15px;
  transition: all 0.2s;
}

.table-input:focus {
  outline: none;
  border-color: #4F46E5;
}

.table-input:disabled,
.table-input:read-only {
  background: #f9fafb;
  color: #9ca3af;
}

.table-select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 15px;
  transition: all 0.2s;
  background: white;
  cursor: pointer;
}

.table-select:focus {
  outline: none;
  border-color: #4F46E5;
}

.table-select:disabled {
  background: #f9fafb;
  color: #9ca3af;
  cursor: not-allowed;
}

.btn-delete {
  padding: 8px 12px;
  background: none;
  border: 1px solid #ef4444;
  color: #ef4444;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
}

.btn-delete:hover {
  background: #ef4444;
  color: white;
}

.btn-delete:disabled {
  border-color: #d1d5db;
  color: #d1d5db;
  cursor: not-allowed;
  background: none;
}

.btn-add-subject {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-add-subject:hover {
  background: #059669;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-add-subject:disabled {
  background: #d1d5db;
  cursor: not-allowed;
  transform: none;
}

.text-center {
  text-align: center;
}

.text-muted {
  color: #9ca3af;
  font-style: italic;
}

.text-danger {
  color: #ef4444;
}

.text-success {
  color: #10b981;
}

.text-info {
  color: #3b82f6;
}

.text-warning {
  color: #f59e0b;
}

/* Upload Boxes */
.identity-upload-section {
  margin-bottom: 36px;
  padding: 24px;
  background: #f9fafb;
  border-radius: 10px;
  border: 2px solid #e5e7eb;
}

.subsection-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 16px;
}

.file-upload-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.file-upload-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  background: white;
  border: 2px solid #4F46E5;
  border-radius: 8px;
  color: #4F46E5;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 200px;
}

.file-upload-btn:hover {
  background: #eef2ff;
}

.file-upload-btn input {
  display: none;
}

.file-upload-btn i {
  font-size: 18px;
}

.file-upload-btn span {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-uploaded-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #10b981;
  color: white;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
}

.document-group {
  margin-bottom: 36px;
  padding: 24px;
  background: #f0f9ff;
  border-radius: 10px;
  border: 2px solid #bfdbfe;
}

.upload-box-compact {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  padding: 20px;
  text-align: center;
  transition: all 0.2s;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.upload-box-compact:hover {
  border-color: #4F46E5;
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.1);
}

.doc-label {
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  display: block;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-upload-btn-compact {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: #f9fafb;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.file-upload-btn-compact:hover {
  background: #eef2ff;
  border-color: #4F46E5;
}

.file-upload-btn-compact i {
  font-size: 32px;
  color: #4F46E5;
}

.file-upload-btn-compact span {
  font-size: 13px;
  color: #4F46E5;
  font-weight: 600;
  text-align: center;
  word-break: break-word;
  max-width: 100%;
}

.file-upload-btn-compact input {
  display: none;
}

.file-success {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 6px;
  color: #166534;
  font-weight: 600;
  font-size: 13px;
}

.file-success i {
  color: #10b981;
  font-size: 16px;
}

.upload-box-inline {
  margin-bottom: 0;
}

/* Buttons */
.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.btn-info {
  background: #3b82f6;
  color: white;
}

.btn-info:hover {
  background: #2563eb;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn-danger:hover {
  background: #dc2626;
}

.btn-block {
  width: 100%;
  justify-content: center;
}

.btn:disabled {
  background: #d1d5db;
  cursor: not-allowed;
  transform: none;
}

/* Submit Section */
.submit-section {
  margin-top: 48px;
  padding: 28px;
  background: #f9fafb;
  border-radius: 12px;
  border: 2px solid #e5e7eb;
}

.submit-info {
  margin-bottom: 24px;
}

.submit-info p {
  color: #4b5563;
  line-height: 1.7;
  margin: 0;
  font-size: 15px;
}

.btn-submit {
  width: 100%;
  padding: 16px 32px;
  background: linear-gradient(135deg, #4F46E5 0%, #6366F1 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(79, 70, 229, 0.3);
}

.btn-submit:disabled {
  background: #d1d5db;
  cursor: not-allowed;
  transform: none;
}

/* Status Timeline */
.status-timeline {
  margin: 36px 0;
}

.status-step {
  display: flex;
  gap: 24px;
  padding-bottom: 48px;
  position: relative;
}

.status-step::after {
  content: '';
  position: absolute;
  left: 26px;
  top: 56px;
  width: 3px;
  height: calc(100% - 56px);
  background: #e5e7eb;
}

.status-step:last-child::after {
  display: none;
}

.status-step.completed::after {
  background: #10b981;
}

.status-step.rejected::after {
  background: #ef4444;
}

.status-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 24px;
  flex-shrink: 0;
  z-index: 1;
}

.status-step.completed .status-icon {
  background: #10b981;
  color: white;
}

.status-step.rejected .status-icon {
  background: #ef4444;
  color: white;
}

.status-content {
  flex: 1;
}

.status-content h3 {
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 10px 0;
}

.status-content p {
  margin: 6px 0;
  color: #6b7280;
  font-size: 15px;
  line-height: 1.6;
}

.roll-number-box {
  margin: 10px 0;
  padding: 20px;
  background: #f0fdf4;
  border: 2px solid #10b981;
  border-radius: 10px;
}

.roll-number-box label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #166534;
}

.roll-number {
  font-size: 28px;
  font-weight: 700;
  color: #10b981;
  letter-spacing: 2px;
}

/* Application Summary */
.application-summary {
  margin-top: 48px;
  padding: 28px;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.application-summary h3 {
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 24px 0;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.summary-item label {
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
}

.summary-item span {
  font-size: 16px;
  color: #1f2937;
  font-weight: 500;
}

.payment-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15) !important;
  border-color: #3498db !important;
}

.payment-card:active {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12) !important;
}


/* Responsive Design */
@media (max-width: 768px) {
  .content-card {
    padding: 24px;
  }

  .section-title {
    font-size: 26px;
  }

  .tab-btn span {
    font-size: 14px;
  }

  .tab-btn {
    padding: 16px 20px;
  }

  .payment-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .app-info {
    font-size: 14px;
  }

  .app-id {
    font-size: 16px;
  }

  .form-section-title {
    font-size: 20px;
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }

  .btn-submit {
    font-size: 16px;
    padding: 14px 24px;
  }

  .payment-card {
    min-height: 120px !important;
    padding: 15px 10px !important;
  }

  .payment-card-logo img {
    height: 50px !important;
  }

  .payment-card-title {
    font-size: 12px !important;
  }
}

@media (max-width: 576px) {
  .tab-content-wrapper {
    padding: 24px 12px;
  }

  .content-card {
    padding: 20px;
  }

  .subject-table {
    font-size: 13px;
  }

  .subject-table th,
  .subject-table td {
    padding: 10px;
  }

  .upload-box {
    padding: 20px;
  }

  .btn-logout span,
  .app-label {
    display: none;
  }

  .header-content {
    padding: 0 12px;
  }

  .tab-nav-wrapper {
    padding: 0 12px;
  }

  .payment-options-container h5 {
    font-size: 16px !important;
    margin-bottom: 20px !important;
  }

  .payment-card {
    min-height: 110px !important;
    padding: 12px 8px !important;
  }

  .payment-card-logo img {
    height: 45px !important;
  }

  .payment-card-title {
    font-size: 11px !important;
    margin-top: 5px !important;
  }
}
</style>